// Iter-207bf — Reminder Setup with inline editing.
//
// Each rule card has a "Modifier" toggle that flips fields to editable.
// "Sauvegarder" calls PUT /api/settings with the changed fields only.
// Covers ALL parameters: enabled flag, day-of-week, hour, subject + body
// templates, AND for the monthly card the cutoff day-N + escalation delay.
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, CalendarClock, Mail, BellRing, FileText, Settings as Cog,
  AlertTriangle, Save, X,
} from "lucide-react";
import IconAction from "@/components/IconAction";
import { api } from "../lib/api";
import { toast } from "sonner";

const DOW_FR = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

export default function CraReminderSetup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/cra-reminder/setup");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveAndReload = async (payload) => {
    try {
      await api.put("/settings", payload);
      toast.success("Paramètres mis à jour.");
      await load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la sauvegarde");
      return false;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-zinc-500" data-testid="reminder-setup-loading">
        <Loader2 size={14} className="animate-spin" /> Chargement…
      </div>
    );
  }
  if (!data) return null;
  const w = data.weekly_cra;
  const ew = data.weekly_emptyweek;
  const m = data.monthly_cra;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="reminder-setup-page">
      <header className="space-y-1">
        <h1 className="text-2xl font-light tracking-tight" data-testid="reminder-setup-title">
          Reminder Setup
        </h1>
        <p className="text-sm text-zinc-500 leading-relaxed">
          Vue & édition de toutes les règles de relance CRA. Cliquez sur
          « Modifier » pour ajuster les paramètres directement ici.
        </p>
      </header>

      <WeeklyCard data={w} onSave={saveAndReload} />
      <EmptyWeekCard data={ew} onSave={saveAndReload} />
      <MonthlyCard data={m} onSave={saveAndReload} />

      <div className="text-xs text-zinc-500 pt-2 border-t border-zinc-100">
        Besoin d'envoyer une relance ciblée à un collaborateur précis ?{" "}
        <Link to="/admin/cra-reminder-manual" className="text-blue-700 hover:underline" data-testid="reminder-setup-link-manual">
          → Manual CRA Reminder
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card scaffold (header + edit toggle)
// ---------------------------------------------------------------------------
function Card({ title, icon: Icon, enabled, children, testid, isEditing, onEdit, onCancel, onSave, saving }) {
  return (
    <section className="border border-zinc-200 rounded-md bg-white" data-testid={testid}>
      <header className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
        <div className="flex items-center gap-2">
          <Icon size={15} className="text-zinc-700" />
          <h2 className="text-sm font-medium text-zinc-900">{title}</h2>
          {enabled === true && (
            <span className="inline-block px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded bg-emerald-50 text-emerald-700 border border-emerald-100">Actif</span>
          )}
          {enabled === false && (
            <span className="inline-block px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded bg-zinc-100 text-zinc-600 border border-zinc-200">Désactivé</span>
          )}
        </div>
        {isEditing ? (
          <div className="flex items-center gap-1">
            <IconAction icon={X} testid={`${testid}-cancel`} label="Annuler la modification" onClick={onCancel} />
            <IconAction icon={Save} tone="dark" testid={`${testid}-save`} label="Sauvegarder" loading={saving} onClick={onSave} />
          </div>
        ) : (
          <IconAction icon={Cog} testid={`${testid}-edit`} label="Modifier ce réglage" onClick={onEdit} />
        )}
      </header>
      <div className="p-4 space-y-3">{children}</div>
    </section>
  );
}

function ViewRule({ icon: Icon, label, day, hour, description, testid }) {
  return (
    <div className="border border-zinc-100 rounded p-3 bg-zinc-50/40" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-zinc-800">
        <Icon size={13} />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className="text-xs font-mono mt-1 text-zinc-700">
        {day} · {String(hour).padStart(2, "0")}:00 UTC
      </div>
      <p className="text-[11px] text-zinc-600 leading-snug mt-1.5">{description}</p>
    </div>
  );
}

function Mini({ label, value, testid }) {
  return (
    <div className="p-2 rounded border border-zinc-100 bg-zinc-50/40" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-800 mt-0.5">{value || "—"}</div>
    </div>
  );
}

function DowSelect({ value, onChange, testid }) {
  return (
    <select
      value={value} onChange={(e) => onChange(parseInt(e.target.value, 10))}
      data-testid={testid}
      className="h-7 border border-zinc-200 rounded px-2 text-xs"
    >
      {DOW_FR.map((d, i) => <option key={i} value={i}>{d}</option>)}
    </select>
  );
}

function HourSelect({ value, onChange, testid }) {
  return (
    <select
      value={value} onChange={(e) => onChange(parseInt(e.target.value, 10))}
      data-testid={testid}
      className="h-7 border border-zinc-200 rounded px-2 text-xs font-mono"
    >
      {Array.from({ length: 24 }, (_, i) => i).map((h) => (
        <option key={h} value={h}>{String(h).padStart(2, "0")}:00 UTC</option>
      ))}
    </select>
  );
}

function FieldLabel({ children }) {
  return <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">{children}</div>;
}

// ---------------------------------------------------------------------------
// Weekly auto reminder
// ---------------------------------------------------------------------------
function WeeklyCard({ data, onSave }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    enabled: data.enabled,
    inapp_day: data.inapp.day, inapp_hour: data.inapp.hour,
    email_day: data.email.day, email_hour: data.email.hour,
    subject: data.templates.subject, body: data.templates.body,
  });

  const begin = () => {
    setDraft({
      enabled: data.enabled,
      inapp_day: data.inapp.day, inapp_hour: data.inapp.hour,
      email_day: data.email.day, email_hour: data.email.hour,
      subject: data.templates.subject, body: data.templates.body,
    });
    setEditing(true);
  };

  const submit = async () => {
    setSaving(true);
    const ok = await onSave({
      weekly_cra_reminder_enabled: !!draft.enabled,
      weekly_cra_reminder_inapp_day: draft.inapp_day,
      weekly_cra_reminder_inapp_hour: draft.inapp_hour,
      weekly_cra_reminder_email_day: draft.email_day,
      weekly_cra_reminder_email_hour: draft.email_hour,
      weekly_cra_subject_template: draft.subject || undefined,
      weekly_cra_body_template: draft.body || undefined,
    });
    setSaving(false);
    if (ok) setEditing(false);
  };

  return (
    <Card
      title="Weekly CRA — Relance auto"
      icon={CalendarClock}
      enabled={data.enabled}
      testid="reminder-card-weekly"
      isEditing={editing}
      onEdit={begin}
      onCancel={() => setEditing(false)}
      onSave={submit}
      saving={saving}
    >
      {!editing ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ViewRule icon={BellRing} label="Notification in-app" day={data.inapp.day_label}
                      hour={data.inapp.hour} description={data.inapp.description}
                      testid="reminder-weekly-inapp" />
            <ViewRule icon={Mail} label="Email Gmail" day={data.email.day_label}
                      hour={data.email.hour} description={data.email.description}
                      testid="reminder-weekly-email" />
          </div>
          <TemplateBlock subject={data.templates.subject} body={data.templates.body} testid="reminder-weekly-templates" />
        </>
      ) : (
        <div className="space-y-3" data-testid="reminder-weekly-editor">
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={draft.enabled}
                   onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
                   data-testid="reminder-weekly-edit-enabled" />
            <span>Relance hebdomadaire activée</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <FieldLabel>Notification in-app</FieldLabel>
              <div className="flex items-center gap-2">
                <DowSelect value={draft.inapp_day} onChange={(v) => setDraft((d) => ({ ...d, inapp_day: v }))}
                            testid="reminder-weekly-edit-inapp-day" />
                <HourSelect value={draft.inapp_hour} onChange={(v) => setDraft((d) => ({ ...d, inapp_hour: v }))}
                             testid="reminder-weekly-edit-inapp-hour" />
              </div>
            </div>
            <div>
              <FieldLabel>Email Gmail</FieldLabel>
              <div className="flex items-center gap-2">
                <DowSelect value={draft.email_day} onChange={(v) => setDraft((d) => ({ ...d, email_day: v }))}
                            testid="reminder-weekly-edit-email-day" />
                <HourSelect value={draft.email_hour} onChange={(v) => setDraft((d) => ({ ...d, email_hour: v }))}
                             testid="reminder-weekly-edit-email-hour" />
              </div>
            </div>
          </div>
          <TemplateEditor draft={draft} setDraft={setDraft} testid="reminder-weekly-edit-templates" />
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Empty-week reminder
// ---------------------------------------------------------------------------
function EmptyWeekCard({ data, onSave }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    enabled: data.enabled, day: data.day, hour: data.hour,
    subject: data.templates.subject, body: data.templates.body,
  });
  const begin = () => {
    setDraft({
      enabled: data.enabled, day: data.day, hour: data.hour,
      subject: data.templates.subject, body: data.templates.body,
    });
    setEditing(true);
  };
  const submit = async () => {
    setSaving(true);
    const ok = await onSave({
      weekly_cra_emptyweek_reminder_enabled: !!draft.enabled,
      weekly_cra_emptyweek_reminder_day: draft.day,
      weekly_cra_emptyweek_reminder_hour: draft.hour,
      weekly_cra_emptyweek_subject_template: draft.subject || undefined,
      weekly_cra_emptyweek_body_template: draft.body || undefined,
    });
    setSaving(false);
    if (ok) setEditing(false);
  };
  return (
    <Card
      title="Weekly CRA — Relance « semaine vide »"
      icon={AlertTriangle}
      enabled={data.enabled}
      testid="reminder-card-emptyweek"
      isEditing={editing}
      onEdit={begin}
      onCancel={() => setEditing(false)}
      onSave={submit}
      saving={saving}
    >
      {!editing ? (
        <>
          <ViewRule icon={Mail} label="Email d'alerte semaine vide" day={data.day_label}
                    hour={data.hour} description={data.description}
                    testid="reminder-emptyweek-detail" />
          <TemplateBlock subject={data.templates.subject} body={data.templates.body} testid="reminder-emptyweek-templates" />
        </>
      ) : (
        <div className="space-y-3" data-testid="reminder-emptyweek-editor">
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={draft.enabled}
                   onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
                   data-testid="reminder-emptyweek-edit-enabled" />
            <span>Relance « semaine vide » activée</span>
          </label>
          <div className="flex items-center gap-2">
            <DowSelect value={draft.day} onChange={(v) => setDraft((d) => ({ ...d, day: v }))}
                        testid="reminder-emptyweek-edit-day" />
            <HourSelect value={draft.hour} onChange={(v) => setDraft((d) => ({ ...d, hour: v }))}
                         testid="reminder-emptyweek-edit-hour" />
          </div>
          <TemplateEditor draft={draft} setDraft={setDraft} testid="reminder-emptyweek-edit-templates" />
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Monthly CRA cutoff + escalation
// ---------------------------------------------------------------------------
function MonthlyCard({ data, onSave }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    cutoff_business_day: data.cutoff_business_day || 2,
    escalation_days_after_cutoff: data.escalation_days_after_cutoff || 5,
    subject: data.templates.subject, body: data.templates.body,
  });
  const begin = () => {
    setDraft({
      cutoff_business_day: data.cutoff_business_day || 2,
      escalation_days_after_cutoff: data.escalation_days_after_cutoff || 5,
      subject: data.templates.subject, body: data.templates.body,
    });
    setEditing(true);
  };
  const submit = async () => {
    if (!draft.subject?.trim() || !draft.body?.trim()) {
      toast.error("Sujet et corps de l'email sont obligatoires.");
      return;
    }
    setSaving(true);
    const ok = await onSave({
      monthly_cra_cutoff_business_day: draft.cutoff_business_day,
      monthly_cra_escalation_days_after_cutoff: draft.escalation_days_after_cutoff,
      monthly_cra_subject_template: draft.subject,
      monthly_cra_body_template: draft.body,
    });
    setSaving(false);
    if (ok) setEditing(false);
  };
  return (
    <Card
      title="Monthly CRA — Date limite & escalade"
      icon={CalendarClock}
      enabled={null}
      testid="reminder-card-monthly"
      isEditing={editing}
      onEdit={begin}
      onCancel={() => setEditing(false)}
      onSave={submit}
      saving={saving}
    >
      {!editing ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <Mini label="Règle de date limite" value={data.cutoff_rule_label} testid="reminder-monthly-cutoff-rule" />
            <Mini
              label="Période en cours (M − 1)"
              value={`${data.current_period.month_label} ${data.current_period.year}`}
              testid="reminder-monthly-period"
            />
            <Mini
              label="Date limite"
              value={`${data.current_period.cutoff_date} (J+${data.current_period.days_past_cutoff})`}
              testid="reminder-monthly-cutoff"
            />
          </div>
          <p className="text-xs text-zinc-600 leading-relaxed">
            <strong>Escalade automatique :</strong> {data.escalation_rule_label}
          </p>
          <TemplateBlock subject={data.templates.subject} body={data.templates.body} testid="reminder-monthly-templates" />
          <div className="pt-2">
            <Link
              to="/admin/cra-late"
              className="inline-flex items-center gap-1.5 px-3 h-7 border border-zinc-200 bg-white text-[11px] font-mono uppercase tracking-wider text-zinc-700 rounded-md hover:bg-zinc-50"
              data-testid="reminder-monthly-link-cra-late"
            >
              Voir la liste des collaborateurs en retard
            </Link>
          </div>
        </>
      ) : (
        <div className="space-y-3" data-testid="reminder-monthly-editor">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <FieldLabel>Date limite (n-ième jour ouvré du mois M+1)</FieldLabel>
              <input
                type="number" min={1} max={20}
                value={draft.cutoff_business_day}
                onChange={(e) => setDraft((d) => ({ ...d, cutoff_business_day: parseInt(e.target.value || "0", 10) }))}
                data-testid="reminder-monthly-edit-cutoff-day"
                className="h-7 w-24 border border-zinc-200 rounded px-2 text-xs font-mono"
              />
            </div>
            <div>
              <FieldLabel>Escalade auto après (jours ouvrés)</FieldLabel>
              <input
                type="number" min={0} max={60}
                value={draft.escalation_days_after_cutoff}
                onChange={(e) => setDraft((d) => ({ ...d, escalation_days_after_cutoff: parseInt(e.target.value || "0", 10) }))}
                data-testid="reminder-monthly-edit-escalation-days"
                className="h-7 w-24 border border-zinc-200 rounded px-2 text-xs font-mono"
              />
            </div>
          </div>
          <TemplateEditor draft={draft} setDraft={setDraft} testid="reminder-monthly-edit-templates" />
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Reusable read-only / editor blocks for subject + body
// ---------------------------------------------------------------------------
function TemplateBlock({ subject, body, testid }) {
  const [open, setOpen] = useState(false);
  if (!subject && !body) return null;
  return (
    <div className="border-t border-zinc-100 pt-2" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-zinc-500 hover:text-zinc-900"
        data-testid={`${testid}-toggle`}
      >
        <FileText size={11} /> {open ? "Masquer" : "Voir"} le modèle de message
      </button>
      {open && (
        <div className="mt-2 space-y-1.5 text-xs">
          {subject && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Sujet</div>
              <div className="font-mono text-zinc-800 bg-zinc-50 px-2 py-1 rounded border border-zinc-100">{subject}</div>
            </div>
          )}
          {body && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Corps</div>
              <pre className="font-mono text-zinc-700 bg-zinc-50 px-2 py-1.5 rounded border border-zinc-100 whitespace-pre-wrap text-[11px] leading-relaxed">{body}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ draft, setDraft, testid }) {
  return (
    <div className="space-y-2 border-t border-zinc-100 pt-2" data-testid={testid}>
      <div>
        <FieldLabel>Sujet de l'email</FieldLabel>
        <input
          type="text" value={draft.subject || ""}
          onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
          className="w-full h-8 border border-zinc-200 rounded px-2 text-xs font-mono"
          data-testid={`${testid}-subject`}
        />
      </div>
      <div>
        <FieldLabel>Corps de l'email</FieldLabel>
        <textarea
          rows={6} value={draft.body || ""}
          onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
          className="w-full border border-zinc-200 rounded px-2 py-1.5 text-xs font-mono"
          data-testid={`${testid}-body`}
        />
      </div>
    </div>
  );
}
